import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Phone, Mail, MapPin, FileText, Ruler, Users2, MessageSquare, GitBranch, UserCheck, PhoneCall, Banknote, Trash2, ClipboardList, Send, Clock, Ban } from "lucide-react";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import {
  adminHttp, STATUS_LABELS, STATUS_COLORS, STATUS_ORDER, scoreColor, formatApiError, LOST_REASON_LABELS,
} from "../../lib/adminApi";
import { useAuth } from "../AuthContext";
import LeadEmailsTab from "../components/LeadEmailsTab";

const PROJECT_TYPES = {
  malla_sombra: "Velaria de malla sombra",
  membrana_pvc: "Membrana PVC",
  tensoestructura_arquitectonica: "Tensoestructura arquitectónica",
  techo_canchas: "Techo para canchas",
  cubierta_industrial: "Cubierta industrial",
  plaza_centro_comercial: "Plaza / centro comercial",
  residencial_premium: "Residencial premium",
  institucion_educativa: "Institución educativa",
  otro: "Otro",
};
const SQM_LABEL = { "<100": "< 100 m²", "100-300": "100-300 m²", "300-600": "300-600 m²", "600-1200": "600-1200 m²", ">1200": "> 1200 m²" };

const ACTIVITY_ICON = { status_change: GitBranch, assignment: UserCheck, note: MessageSquare, email_sent: Mail, call: PhoneCall, value_change: Banknote };

export default function LeadDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [estValue, setEstValue] = useState("");
  const [savingEst, setSavingEst] = useState(false);
  const [tab, setTab] = useState("detalle");

  const reload = async () => {
    const { data } = await adminHttp.get(`/admin/leads/${id}`);
    setLead(data);
    setEstValue(data.estimated_value_mxn || "");
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        await reload();
        if (user?.role === "admin") {
          const { data } = await adminHttp.get("/admin/users").catch(() => ({ data: { items: [] } }));
          setUsers(data.items || []);
        } else {
          // Salespeople can still be assigned but only admin fetches full users list
          setUsers([]);
        }
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const patchLead = async (patch, successMsg) => {
    try {
      await adminHttp.patch(`/admin/leads/${id}`, patch);
      await reload();
      if (successMsg) toast.success(successMsg);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    try {
      await adminHttp.post(`/admin/leads/${id}/notes`, { text: noteText.trim() });
      setNoteText("");
      await reload();
      toast.success("Nota agregada");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const deleteNote = async (noteId) => {
    try {
      await adminHttp.delete(`/admin/leads/${id}/notes/${noteId}`);
      await reload();
      toast.success("Nota eliminada");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const logCall = async () => {
    const detail = window.prompt("Resumen breve de la llamada:");
    if (!detail) return;
    try {
      await adminHttp.post(`/admin/leads/${id}/activities`, { type: "call", detail });
      await reload();
      toast.success("Llamada registrada");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const saveEstimate = async () => {
    setSavingEst(true);
    const num = Number(estValue);
    if (Number.isNaN(num) || num < 0) { toast.error("Monto inválido"); setSavingEst(false); return; }
    await patchLead({ estimated_value_mxn: num }, "Valor estimado actualizado");
    setSavingEst(false);
  };

  const softDelete = async () => {
    if (!window.confirm("¿Eliminar este lead? (solo admin)")) return;
    try {
      await adminHttp.delete(`/admin/leads/${id}`);
      toast.success("Lead eliminado");
      window.location.href = "/admin/leads";
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (loading || !lead) return <div className="p-8 text-[#6B7280] font-mono text-xs uppercase tracking-widest">Cargando…</div>;

  const activities = [...(lead.activities || [])].reverse();

  return (
    <div className="p-6 lg:p-10" data-testid="admin-lead-detail">
      <Link to="/admin/leads" className="inline-flex items-center gap-2 font-mono uppercase text-[10px] tracking-widest text-[#4B5563] hover:text-accent mb-4">
        <ArrowLeft className="w-3 h-3" /> Volver a leads
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <span className="divider-accent" /><span className="kicker">Lead</span>
          </div>
          <h1 className="font-display font-black tracking-tightest text-3xl md:text-5xl">{lead.full_name || lead.email || "Lead sin nombre"}</h1>
          <p className="font-mono text-xs text-[#6B7280] mt-2">
            Creado {format(parseISO(lead.created_at), "d MMM yyyy · HH:mm", { locale: es })} · ID: {lead.id.slice(0, 8)}
          </p>
        </div>
        <div className="flex items-center gap-4">
          {lead.tier && (
            <div className="text-right" data-testid="lead-tier-badge">
              <p className="font-mono text-[10px] uppercase tracking-widest text-[#6B7280]">Tier</p>
              <span
                className="mt-1 inline-block px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider"
                style={{
                  background: lead.tier === "premium" ? "#3d2310" : lead.tier === "medium" ? "#1c2a4d" : "#2a2a2a",
                  color: lead.tier === "premium" ? "#8cb62c" : lead.tier === "medium" ? "#8aa9ff" : "#bababa",
                }}
              >
                {lead.tier === "premium" ? "🏆 Premium" : lead.tier === "medium" ? "Medium" : "Small"}
              </span>
              {lead.estimated_value_mxn ? (
                <p className="mt-1 font-mono text-[10px] text-[#6B7280]">
                  ~ ${Number(lead.estimated_value_mxn).toLocaleString("es-MX")} MXN
                </p>
              ) : null}
            </div>
          )}
          <div className="text-right">
            <p className="font-mono text-[10px] uppercase tracking-widest text-[#6B7280]">Score</p>
            <p className="font-display font-black text-5xl tabular-nums" style={{ color: scoreColor(lead.score || 0) }}>{lead.score || 0}</p>
          </div>
          {user?.role === "admin" && (
            <button onClick={softDelete} className="p-3 border border-[#DDE2D5] text-[#6B7280] hover:border-red-500 hover:text-red-500" data-testid="lead-delete">
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-[1.3fr_1fr] gap-6">
        {/* LEFT — Tabs + content */}
        <div className="space-y-6">
          <div className="flex gap-1 border-b border-[#DDE2D5]" data-testid="lead-tabs">
            {[
              { id: "detalle", label: "Detalle" },
              { id: "emails", label: "Emails" },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`px-5 py-3 font-mono text-[11px] uppercase tracking-widest border-b-2 transition ${tab === t.id ? "border-accent text-accent" : "border-transparent text-[#6B7280] hover:text-[#1F2937]"}`}
                data-testid={`lead-tab-${t.id}`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === "emails" ? (
            <LeadEmailsTab leadId={id} leadEmail={lead.email} leadName={lead.full_name} />
          ) : (<>
          <div className="bg-white border border-[#DDE2D5] p-6">
            <h3 className="font-display font-black text-lg mb-4">Datos del contacto</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <Info icon={Phone} label="Teléfono">
                {lead.phone ? (
                  <a href={`https://wa.me/${lead.phone.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" className="text-[#1F2937] hover:text-accent">{lead.phone}</a>
                ) : "—"}
              </Info>
              <Info icon={Mail} label="Email">{lead.email ? <a href={`mailto:${lead.email}`} className="text-[#1F2937] hover:text-accent">{lead.email}</a> : "—"}</Info>
              <Info icon={MapPin} label="Ciudad">{lead.city || "—"}</Info>
              <Info icon={Ruler} label="Metros cuadrados">{SQM_LABEL[lead.square_meters] || "—"}</Info>
              <Info icon={ClipboardList} label="Tipo de proyecto">{PROJECT_TYPES[lead.project_type] || "—"}</Info>
              <Info icon={FileText} label="Planos">{lead.has_plans === "si" ? "Sí, tiene" : lead.has_plans === "proceso" ? "En proceso" : lead.has_plans === "no" ? "Aún no" : "—"}</Info>
            </div>
            {lead.description && (
              <div className="mt-5 pt-5 border-t border-[#DDE2D5]">
                <p className="font-mono text-[10px] uppercase tracking-widest text-[#6B7280] mb-2">Descripción</p>
                <p className="text-[#374151] text-sm leading-relaxed whitespace-pre-wrap">{lead.description}</p>
              </div>
            )}
          </div>

          <div className="bg-white border border-[#DDE2D5] p-6">
            <h3 className="font-display font-black text-lg mb-4">Tracking / origen</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <Info label="Fuente">{lead.source}</Info>
              <Info label="UTM source">{lead.utm_source || "direct"}</Info>
              <Info label="UTM medium">{lead.utm_medium || "—"}</Info>
              <Info label="UTM campaign">{lead.utm_campaign || "—"}</Info>
              <Info label="UTM term">{lead.utm_term || "—"}</Info>
              <Info label="UTM content">{lead.utm_content || "—"}</Info>
              <Info label="Referrer" className="sm:col-span-2">{lead.referrer || "directo"}</Info>
              <Info label="Landing" className="sm:col-span-2">{lead.landing_page || "—"}</Info>
              <Info label="User agent" className="sm:col-span-2"><span className="truncate block font-mono text-xs text-[#6B7280]">{lead.user_agent || "—"}</span></Info>
            </div>
          </div>

          {/* Timeline */}
          <div className="bg-white border border-[#DDE2D5] p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display font-black text-lg">Timeline</h3>
              <button onClick={logCall} className="font-mono uppercase text-[10px] tracking-widest text-accent hover:underline" data-testid="lead-log-call">
                <PhoneCall className="inline w-3 h-3 mr-1" /> Registrar llamada
              </button>
            </div>
            <div className="space-y-4" data-testid="lead-timeline">
              {activities.length === 0 && <p className="text-[#6B7280] text-sm">Sin actividad aún.</p>}
              {activities.map((a) => {
                const Icon = ACTIVITY_ICON[a.type] || MessageSquare;
                return (
                  <div key={a.id} className="flex gap-4">
                    <div className="w-8 h-8 flex-shrink-0 bg-eclipse border border-accent/40 text-accent grid place-items-center">
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 pb-4 border-b border-[#EAECEA] last:border-b-0">
                      <p className="text-sm text-[#1F2937]">{a.detail}</p>
                      <p className="font-mono text-[10px] uppercase tracking-widest text-[#6B7280] mt-1">
                        {format(parseISO(a.created_at), "d MMM · HH:mm", { locale: es })} · {a.actor_name || "—"}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Notes */}
          <div className="bg-white border border-[#DDE2D5] p-6">
            <h3 className="font-display font-black text-lg mb-4">Notas</h3>
            <div className="space-y-3 mb-4" data-testid="lead-notes-list">
              {(lead.notes || []).length === 0 && <p className="text-[#6B7280] text-sm">Sin notas.</p>}
              {(lead.notes || []).map((n) => (
                <div key={n.id} className="bg-[#F2F4ED] border border-[#EAECEA] p-4 flex items-start gap-3">
                  <MessageSquare className="w-4 h-4 text-accent mt-1 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[#1F2937] whitespace-pre-wrap">{n.text}</p>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-[#6B7280] mt-2">
                      {n.author_name} · {format(parseISO(n.created_at), "d MMM · HH:mm", { locale: es })}
                    </p>
                  </div>
                  {(user?.role === "admin" || n.author_id === user?.id) && (
                    <button onClick={() => deleteNote(n.id)} className="text-[#9CA3AF] hover:text-red-400" aria-label="Borrar nota">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <textarea
              rows={3}
              placeholder="Escribe una nota…"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              className="w-full bg-white border border-[#DDE2D5] px-4 py-3 text-[#1F2937] text-sm placeholder:text-[#6B7280] focus:outline-none focus:border-accent resize-none"
              data-testid="lead-note-input"
            />
            <button onClick={addNote} className="btn-accent mt-3 py-3 px-5 text-xs" data-testid="lead-note-add">
              Agregar nota
            </button>
          </div>
          </>)}
        </div>

        {/* RIGHT — Controls */}
        <div className="space-y-6">
          <div className="bg-white border border-[#DDE2D5] p-6 sticky top-20">
            <div>
              <span className="kicker">Status</span>
              <div className="mt-3 grid grid-cols-2 gap-2" data-testid="lead-status-selector">
                {STATUS_ORDER.map((s) => (
                  <button
                    key={s}
                    onClick={() => patchLead({ status: s }, "Status actualizado")}
                    className="px-3 py-2 font-mono text-[10px] uppercase tracking-wider border transition-colors"
                    style={{
                      background: lead.status === s ? STATUS_COLORS[s] : "transparent",
                      color: lead.status === s ? "#1F2937" : STATUS_COLORS[s],
                      borderColor: STATUS_COLORS[s],
                    }}
                    data-testid={`status-option-${s}`}
                  >
                    {STATUS_LABELS[s]}
                  </button>
                ))}
              </div>
            </div>

            {lead.status === "lost" && (
              <div className="mt-6">
                <span className="kicker">Razón de pérdida</span>
                <select
                  value={lead.lost_reason || ""}
                  onChange={(e) => patchLead({ lost_reason: e.target.value }, "Razón guardada")}
                  className="mt-3 w-full bg-white border border-[#DDE2D5] px-3 py-3 text-[#1F2937] text-sm focus:outline-none focus:border-accent"
                  data-testid="lead-lost-reason"
                >
                  <option value="">—</option>
                  {Object.entries(LOST_REASON_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
            )}

            <div className="mt-6">
              <span className="kicker flex items-center gap-2"><Users2 className="w-3 h-3" /> Asignado a</span>
              {users.length > 0 ? (
                <select
                  value={lead.assigned_to || ""}
                  onChange={(e) => patchLead({ assigned_to: e.target.value || null }, "Asignación actualizada")}
                  className="mt-3 w-full bg-white border border-[#DDE2D5] px-3 py-3 text-[#1F2937] text-sm focus:outline-none focus:border-accent"
                  data-testid="lead-assign-selector"
                >
                  <option value="">Sin asignar</option>
                  {users.map((u) => <option key={u.id} value={u.id}>{u.full_name} ({u.role})</option>)}
                </select>
              ) : (
                <p className="mt-3 text-xs text-[#6B7280] font-mono">
                  {lead.assigned_to_user?.full_name || "Sin asignar"} · Solo admin puede reasignar.
                </p>
              )}
            </div>

            <div className="mt-6">
              <span className="kicker flex items-center gap-2"><Banknote className="w-3 h-3" /> Valor estimado MXN</span>
              <div className="mt-3 flex gap-2">
                <input
                  type="number"
                  min={0}
                  value={estValue}
                  onChange={(e) => setEstValue(e.target.value)}
                  placeholder="0"
                  className="flex-1 bg-eclipse border border-[#DDE2D5] px-3 py-3 text-[#1F2937] text-sm font-mono focus:outline-none focus:border-accent"
                  data-testid="lead-estimate-input"
                />
                <button onClick={saveEstimate} disabled={savingEst} className="btn-accent py-3 px-4 text-xs disabled:opacity-60" data-testid="lead-estimate-save">
                  {savingEst ? "…" : "Guardar"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Info({ icon: Icon, label, children, className = "" }) {
  return (
    <div className={className}>
      <p className="font-mono text-[10px] uppercase tracking-widest text-[#6B7280] flex items-center gap-1.5">
        {Icon && <Icon className="w-3 h-3" />}{label}
      </p>
      <div className="mt-1 text-[#1F2937] text-sm">{children}</div>
    </div>
  );
}
